'use client'

import { createContext, useContext, type ReactNode } from 'react'
import { useLocale } from 'next-intl'
import { useSearchParams } from 'next/navigation'

import type { DisplayCurrency } from './map'
import { selectedDisplayCurrency } from './resolve'

const DisplayCurrencyContext = createContext<DisplayCurrency | undefined>(undefined)

/**
 * Makes the page's **selected** display currency — the non-default one its address named —
 * readable anywhere in the tree, so a link deep in the chrome can carry it without the
 * value being threaded through props.
 *
 * The selection is read from **the address the browser is currently at**, which is the only
 * thing a page's currency is a property of — never handed in, so there is no second way to
 * be in this state and no way to publish a currency the address could not have named.
 *
 * Reading it here rather than receiving a value the frame resolved is what keeps the
 * selection honest across a **client-side navigation**. App Router does not re-execute a
 * shared layout for a navigation beneath it, so a value resolved once at request time
 * survives onto pages whose address names no currency — the control reporting a currency
 * the reader never chose, and every link on that page carrying it. `useSearchParams` is
 * router state, so it tracks the address bar instead.
 *
 * Reading the query here is safe because no route in this app prerenders — every one is
 * server-rendered on demand, so `useSearchParams` has real values during SSR and needs no
 * Suspense boundary. A statically generated route would need one, and Next fails that
 * build loudly, saying so.
 *
 * The hook is nullable by contract — Next returns null wherever no route context has
 * resolved — and the shell must not be what takes the page down if that happens, so the
 * absence reads as no selection.
 */
export function DisplayCurrencyProvider({ children }: { children: ReactNode }) {
    const locale = useLocale()
    const searchParams = useSearchParams()

    return (
        <DisplayCurrencyContext.Provider value={selectedDisplayCurrency(locale, searchParams)}>
            {children}
        </DisplayCurrencyContext.Provider>
    )
}

/**
 * The page's selected display currency, or `undefined` for none. Read from a tree with no
 * provider — a test, a story, the global error boundary — it reports no selection rather
 * than throwing, so a `Link` outside the frame renders its address unchanged.
 */
export function useDisplayCurrency(): DisplayCurrency | undefined {
    return useContext(DisplayCurrencyContext)
}
